import { useCallback, useSyncExternalStore } from "react";
import { RowFieldErrors } from "../../validation";

const EMPTY: RowFieldErrors = Object.freeze({});

const isSameErrors = (a: RowFieldErrors, b: RowFieldErrors) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => a[key] === b[key]);
};

/**
 * Lỗi validate của bảng CCUS062 được giữ NGOÀI React state.
 *
 * Lý do: validate chạy theo từng ký tự gõ. Nếu để trong useState của handler thì mỗi
 * lần set sẽ re-render từ CCUS061Component xuống cả <Table>, tức vẽ lại toàn bộ các
 * dòng. Ở đây mỗi dòng tự subscribe theo key của nó nên chỉ dòng có lỗi thay đổi mới
 * render lại.
 */
export class RowErrorStore {
  private errors: { [rowKey: string]: RowFieldErrors } = {};
  private listeners = new Map<string, Set<() => void>>();

  subscribe = (rowKey: string, onChange: () => void) => {
    let group = this.listeners.get(rowKey);
    if (!group) {
      group = new Set();
      this.listeners.set(rowKey, group);
    }
    group.add(onChange);

    return () => {
      group?.delete(onChange);
      if (group && group.size === 0) this.listeners.delete(rowKey);
    };
  };

  /** Phải trả reference ổn định, nếu không useSyncExternalStore sẽ lặp vô hạn */
  getRowErrors = (rowKey: string): RowFieldErrors => this.errors[rowKey] ?? EMPTY;

  private notify(rowKey: string) {
    this.listeners.get(rowKey)?.forEach((listener) => listener());
  }

  /** Ghi đè lỗi của 1 dòng. Không đổi gì thì không notify để khỏi render thừa. */
  setRow(rowKey: string | number, fieldErrors: RowFieldErrors) {
    const key = String(rowKey);
    const next = Object.keys(fieldErrors).length === 0 ? EMPTY : fieldErrors;
    if (isSameErrors(this.getRowErrors(key), next)) return;

    if (next === EMPTY) {
      delete this.errors[key];
    } else {
      this.errors[key] = next;
    }
    this.notify(key);
  }

  hasError(rowKey: string | number) {
    return Object.keys(this.getRowErrors(String(rowKey))).length > 0;
  }

  clear() {
    const touchedRows = Object.keys(this.errors);
    this.errors = {};
    touchedRows.forEach((rowKey) => this.notify(rowKey));
  }
}

/** Cell subscribe đúng dòng của mình, không phụ thuộc state của bảng. */
export const useRowErrors = (
  store: RowErrorStore,
  rowKey: string | number
): RowFieldErrors => {
  const key = String(rowKey);
  return useSyncExternalStore(
    useCallback((onChange) => store.subscribe(key, onChange), [store, key]),
    useCallback(() => store.getRowErrors(key), [store, key])
  );
};
